import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';

import Swal from 'sweetalert2';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { CookieService } from 'ngx-cookie-service';

import { MESSAGE_ALGO_SALIO_MAL, show_popup, TITLE_ERROR } from '@shared/utility/popup-error';

import { environment } from '@environments/environment';
import { retryWithDelay } from '@core/operators/retry-delay';

import { RedirectionService } from '@shared/services/redirection.service';
import { AuthService } from '@auth/auth.service';
import { DataValidationService } from './data-validation.service';
import { LoanApplicationService } from '../loan-aplication/loan-application.service';
import { CustomerRecurrenceService, MarketingEventEnumeration, MarketingEventService } from '@shared/services';

import { ModalConditionsAndTermsComponent } from './modal-conditions-and-terms.component';
import { SimulatorComponent } from '../simulator/simulator.component';

import { SimulatorPropertiesType } from '../simulator/models';
import { ConditionTermsSource } from '@core/model/enums/condition-terms-source';
import { DataValidation } from '@model/data-validation';
import { UpdateAmountAndTermRequest } from '@core/model/update-amount-and-term-request';
import { NextFlow } from '@core/model/enums/next-flow';

import { RecurrentStatesEnumeration } from '@shared/services/customer-recurrence/configs/recurrence.constants';
import { ALLIANCE_TYPE_ENUMERATION, SIMULATION_PROPERTIES_ENUMERATION } from '@credit/simulator/config';
import { ROUTES_CONSTANTS } from '@shared/configs';

const REQUEST_MODIFICATION_CONSTANT = 'MODIFICAR TU SOLICITUD';

@Component({
  selector: 'app-your-acceptance',
  templateUrl: './your-acceptance.component.html',
  styleUrls: ['./your-acceptance.component.css'],
})
export class YourAcceptanceComponent implements OnInit {
  public amount: number;
  public term: number;
  public monthFee: number;
  public form: FormGroup;
  public dataValidation: DataValidation;
  public showSimulation: boolean;
  public updateAmountAndTermRequest: UpdateAmountAndTermRequest;
  public showLoader: boolean;
  public domain: string;

  constructor(
    public dialog: MatDialog,
    private readonly formBuilder: FormBuilder,
    private readonly recaptchaV3Service: ReCaptchaV3Service,
    private readonly dataValidationService: DataValidationService,
    private readonly router: Router,
    private readonly redirect: RedirectionService,
    private readonly cookieService: CookieService,
    private readonly loanApplicationService: LoanApplicationService,
    private readonly authService: AuthService,
    private readonly statusService: CustomerRecurrenceService,
    private readonly marketingEventService: MarketingEventService
  ) {
    this.dataValidation = {} as DataValidation;
    this.showLoader = false;
    this.domain = environment.domain;
  }

  public ngOnInit(): void {
    this.form = this.formBuilder.group({
      termsAndConditions: [false, Validators.requiredTrue],
    });
    this.showLoader = false;
    this.setAmountAndTermAndMonthFee();
  }

  public openDialog(): void {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      source: ConditionTermsSource.dataValidation,
    };

    this.dialog.open(ModalConditionsAndTermsComponent, dialogConfig);
  }

  public openSimulator(): void {
    this.dialog
      .open(SimulatorComponent, {
        maxHeight: '95vh',
        maxWidth: '40rem',
        data: {
          buttonLabel: REQUEST_MODIFICATION_CONSTANT,
          type: ALLIANCE_TYPE_ENUMERATION.UPDATE,
        } as SimulatorPropertiesType,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.updateAmountAndTerm(result, this.requestedAmount, this.requestedTerm, this.quotas);
        } else {
          this.setCookiesAmountAndTerm(this.requestedAmount, this.requestedTerm, this.quotas);
        }
      });
  }

  public submit(): void {
    this.showLoader = true;
    this.dataValidationRecurrent();
    let eventName =
      this.statusService.getRecurrenceStatus() === RecurrentStatesEnumeration.IS_RECURRING
        ? MarketingEventEnumeration.RECURRENT_REQUEST_RP
        : MarketingEventEnumeration.NEW_REQUEST_RP;
    this.marketingEventService.sendMarketingEvents(eventName);
  }

  private get requestedAmount(): number {
    return Number(
      this.cookieService.get(SIMULATION_PROPERTIES_ENUMERATION.AMOUNT) ||
        sessionStorage.getItem(SIMULATION_PROPERTIES_ENUMERATION.AMOUNT)
    );
  }

  private get requestedTerm(): number {
    return Number(
      this.cookieService.get(SIMULATION_PROPERTIES_ENUMERATION.TERM) ||
        sessionStorage.getItem(SIMULATION_PROPERTIES_ENUMERATION.TERM)
    );
  }

  private get quotas(): number {
    return Number(
      this.cookieService.get(SIMULATION_PROPERTIES_ENUMERATION.QUOTA) ||
        sessionStorage.getItem(SIMULATION_PROPERTIES_ENUMERATION.QUOTA)
    );
  }

  public setAmountAndTermAndMonthFee(): void {
    this.amount = this.requestedAmount;
    this.term = this.requestedTerm;
    this.monthFee = this.quotas;
  }

  public dataValidationRecurrent(): void {
    this.recaptchaV3Service.execute('data_validation_page').subscribe(
      (token) => {
        this.dataValidation.termAndConditions = this.form.get('termsAndConditions').value;
        if (sessionStorage.getItem('updateAmountOrTerm')) {
          this.dataValidation.amount = this.requestedAmount;
          this.dataValidation.term = this.requestedTerm;
        }
        this.dataValidation.productType = 'RAPIPLAZO';
        this.dataValidation.token = token;
        this.dataValidation.origin = 'DATA_VALIDATION_PAGE';
        this.dataValidationService.DataValidationService(this.dataValidation).subscribe(
          (response) => {
            this.showLoader = false;

            if (response.response.nextFlow == NextFlow.FinalDecisionFlow) {
              this.router.navigate([response.response.urlRedirect]);
            } else if (response.response.nextFlow == NextFlow.DataValidationFlow) {
              show_popup(TITLE_ERROR, response.response.message);
            } else {
              this.router.navigate([ROUTES_CONSTANTS.DECISION_MODEL]);
            }
          },
          (error) => {
            show_popup(TITLE_ERROR, MESSAGE_ALGO_SALIO_MAL).then(() => {
              this.router.navigate(['/dataValidation']);
            });
            this.showLoader = false;
          }
        );
      },
      (error) => {}
    );
  }

  public updateAmountAndTerm(result, beforeAmount: number, beforeTerm: number, beforeQuotas: number): void {
    this.showLoader = true;
    this.loanApplicationService
      .updateAmountAndTerm(this.buildRequestUpdateAmountAndTerm())
      .pipe(retryWithDelay(1000, 3))
      .subscribe({
        next: () => {
          this.monthFee = result.monthFeeValue;
          this.setCookiesAmountAndTerm(result.amount, result.term, result.monthFeeValue);
          this.setAmountAndTermAndMonthFee();
          sessionStorage.setItem('updateAmountOrTerm', 'true');
          this.showLoader = false;
        },
        error: () => {
          this.setCookiesAmountAndTerm(beforeAmount, beforeTerm, beforeQuotas);
          this.showSimulation = false;
          this.showLoader = false;
          this.modalError(null);
        },
      });
  }

  public buildRequestUpdateAmountAndTerm(): UpdateAmountAndTermRequest {
    this.updateAmountAndTermRequest = new UpdateAmountAndTermRequest();
    this.updateAmountAndTermRequest.amount = this.requestedAmount;
    this.updateAmountAndTermRequest.term = this.requestedTerm;
    this.updateAmountAndTermRequest.email = this.authService.getTokenData(this.authService.token).user_name;
    return this.updateAmountAndTermRequest;
  }

  public modalError(urlRedirect): void {
    Swal.fire({
      html: '<b>Lo sentimos ha ocurrido un error,</b><br class="new_line_simulator_error"> te invitamos a intentarlo nuevamente',
      confirmButtonColor: '#4148F5',
      customClass: {
        popup: 'simulator_modal_error',
      },
    }).then((result) => {
      if (urlRedirect) {
        this.router.navigate([urlRedirect]);
      }
    });
  }

  public setCookiesAmountAndTerm(amount: number, term: number, quota: number): void {
    this.cookieService.delete(SIMULATION_PROPERTIES_ENUMERATION.AMOUNT);
    this.cookieService.delete(SIMULATION_PROPERTIES_ENUMERATION.TERM);
    this.cookieService.delete(SIMULATION_PROPERTIES_ENUMERATION.QUOTA);

    sessionStorage.removeItem(SIMULATION_PROPERTIES_ENUMERATION.AMOUNT);
    sessionStorage.removeItem(SIMULATION_PROPERTIES_ENUMERATION.TERM);
    sessionStorage.removeItem(SIMULATION_PROPERTIES_ENUMERATION.QUOTA);

    this.cookieService.set(SIMULATION_PROPERTIES_ENUMERATION.AMOUNT, amount.toString(), 1, '/', this.domain);
    this.cookieService.set(SIMULATION_PROPERTIES_ENUMERATION.TERM, term.toString(), 1, '/', this.domain);
    this.cookieService.set(SIMULATION_PROPERTIES_ENUMERATION.QUOTA, quota.toString(), 1, '/', this.domain);

    sessionStorage.setItem(SIMULATION_PROPERTIES_ENUMERATION.AMOUNT, amount.toString());
    sessionStorage.setItem(SIMULATION_PROPERTIES_ENUMERATION.TERM, term.toString());
    sessionStorage.setItem(SIMULATION_PROPERTIES_ENUMERATION.QUOTA, quota.toString());
  }
}
